import type { ReactNode } from "react";

// StepHeading — consistent screen/step heading: optional eyebrow, serif h1,
// optional supporting paragraph(s). Presentation-only.
//
// The serif-h1 composition has no other DS equivalent (Eyebrow covers only the
// eyebrow). Ink colors ride on arbitrary-value CSS-var classes so the unlayered
// `.dova-*` cascade does not override them.

export interface StepHeadingProps {
    eyebrow?: string;
    title: string;
    /** Supporting paragraph(s) under the title. */
    children?: ReactNode;
    align?: "left" | "center";
}

export function StepHeading({ eyebrow, title, children, align = "left" }: StepHeadingProps) {
    return (
        <div className={`flex flex-col gap-2 ${align === "center" ? "items-center text-center" : ""}`}>
            {eyebrow && <p className="dova-eyebrow">{eyebrow}</p>}
            {/* The DS's own type tokens, --type-h2 climbing to --type-h1, in
                place of `font-serif text-2xl md:text-2xl font-medium
                leading-[1.15]`.

                Two problems with what was here. The `md:` restated the base, so
                it did nothing and the heading was one size at every width. And
                the size utilities do not mean the same thing in both consumers:
                portal-www carries an UNLAYERED `h1 { font: var(--type-h1) }`
                that outranks any @layer utility, so it has been rendering these
                at the scale's h1 all along, while the frontend has no such rule
                and fell through to Tailwind's stock text-2xl at 24px. The first
                screen a client ever sees was a step below the h1 the scale
                defines for exactly this job, and only in one of the two apps.

                Naming the tokens fixes both: they resolve to the same thing
                everywhere, and portal-www's unlayered rule keeps winning there,
                so nothing moves in the portal.

                It climbs rather than starting at 30px because these headings sit
                in a 30rem card with a 24/32px gutter: on a 375px phone a 30px
                serif wraps "How would you describe your relationship?" onto a
                fourth line. Line-height now comes from the tokens (1.25 small,
                1.15 large) rather than a flat 1.15 — the scale's own call, and
                the looser one is the right one at the smaller size. */}
            <h1 className="[font:var(--type-h2)] md:[font:var(--type-h1)] text-[var(--text-primary)] tracking-[-0.015em]">
                {title}
            </h1>
            {children && (
                <div className="[font:var(--type-body)] text-[var(--text-secondary)]">{children}</div>
            )}
        </div>
    );
}
